import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Constants } from 'app/shared/shared-constants';
import { format } from 'app/shared/utils/string.util';
import {
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreWriteSettingControls,
    commonFileTypeStoreDeleteActivityReadSettings,
    commonBinaryStoreWriteSettingControls,
    getCommonBinaryStoreReadSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    cdcDestination
} from '../consts/metadata-consts';
import { binaryMetadata } from '../dataset/binary-metadata';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { ConnectionAuthKind } from 'shell-models-lib';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

const hostPlaceholder = 'e.g. \\\\ServerName\\SharedFolder\\[\\Folder], ' +
    '\\\\<storage name>.file.core.windows.net[\\file service name]';

const dynamicContentControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'host',
            label: stringResources.ModelName_Host,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' },
                {
                    type: 'pattern',
                    pattern: '^(?![hH][tT][tT][pP]:).*',
                    errorMessage: stringResources.FileSystem_HostStartWithHttp_ErrorMessage
                },
                {
                    type: 'pattern',
                    pattern: '^(?![fF][tT][pP]:).*',
                    errorMessage: stringResources.FileSystem_HostStartWithFtp_ErrorMessage
                }
            ],
            isLabelVertical: true,
            description: `${stringResources.FileSystem_Host_Description}\n${hostPlaceholder}`,
            placeholder: hostPlaceholder
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'username',
            label: stringResources.ModelName_UserName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' },
                {
                    type: 'pattern',
                    pattern: '^((?!^[ ]+$).)*$',
                    errorMessage: stringResources.Empty_String_Error_Message
                }
            ],
            isLabelVertical: true
        }
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'parameterizable',
            attribute: 'password',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_Password,
                required: true,
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            }
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template, stringResources.FileLinkedServiceLabel,
            stringResources.ModelName_Password)
    }
];

export const fileServerMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.fileServer.linkedServiceType,
        displayName: stringResources.FileSystem_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-FileSystem'
        },
        isFirstClass: true,
        supportIncrementalBinaryCopy: true,
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            // PQO deliberately create a Folder form of Built-in FolderFiles or FolderContents Kinds.
            // and then PQO update connection into Folder kind when saving their corresponding connection, 
            // thus we need to use either FolderFiles or FolderContents here. And it would create Folder connections.
            id: BuiltInDataSourceOptionId.FolderFiles
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Windows],
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        ...(GlobalVariables.copyJobGenericCdcPhase2 ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportDatasetV2: true,
        copySinkTypes: [adfModelTypeMap.fileSystem.copySinkType],
        copySourceTypes: [adfModelTypeMap.fileSystem.copySourceType],
        supportRootFolder: true,
        supportGetMetadataActivity: true,
        locationPattern: {
            folderPath: '([^]*)/[^/]*',
            fileName: '(?:[^]*/)?([^/]*)'
        },
        mergeReadSettingsInTrident: true,
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        pathRegex: {
            pattern: Constants.fileSharePathValidation,
            message: stringResources.CopyWizard_Dataset_File_Sink_FileSharePathValidationError
        },
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(false, true),
        storeWriteSettingControls: commonFileTypeStoreWriteSettingControls,
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(false, true)
        },
        datasetTypeToStoreWriteSettingControls: {
            [binaryMetadata.type]: commonBinaryStoreWriteSettingControls
        },
        category: ['File'],
        supportFirstClassParameterExperience: true,
        controls: dynamicContentControls
    };
    return metadata;
};
